import isEqual from 'lodash/isEqual';

import {ReportManager} from 'common/report/report-manager';
import {ColorChoice, FunnelBookmark} from 'common/report/funnels/models/types';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {AdditionalContext, isViewAsFlowsContext, QueryContext, SegmentationMetadata} from 'common/query-context';
import {CurrentProject} from 'common/report/model-resources/projects';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {createFunnelDataWithRename} from 'common/report/funnels/util';

import {BookmarkType} from '../queries/app-api/bookmark-client';
import {getReportTracker} from '../report-manager/tracking';
import {ReportName} from '../constants';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {FunnelsQuery} from './models/funnels-query';
import {
  getChartWidgetType,
  getQuerySamplingFactor,
  hasData,
  hasDataTable,
  hasSankeyData,
  usesFlatSelectedSegments,
} from './util';
import {FunnelsQueryBuilderEl} from './query-builder';
import {FunnelsQueryManager} from './query';
import {FunnelsReportHeaderEl} from './report-header';
import {FunnelsVisualizationEl} from './visualization';
import {getQueryFromQueryContext, getReportQueryContext} from './query-builder/utils';
import {getFunnelQueryTrackingProps} from './util/tracking';

export class FunnelsReportManager extends ReportManager<FunnelBookmark, FunnelQueryResponse> {
  bookmarkType = BookmarkType.Funnels;
  reportDisplayName = `Funnels`;

  tracker = getReportTracker({
    appName: ReportName.Funnels,
    productionPrefix: `Funnels`,
  });

  isCsvExportEnabled = true;

  queryBuilderEl = FunnelsQueryBuilderEl;
  reportHeaderEl = FunnelsReportHeaderEl;
  visualizationEl = FunnelsVisualizationEl;

  queryManager = new FunnelsQueryManager({projectId: this.projectId, workspaceId: this.workspaceId});

  public hasUnsavedBookmarkParams(original: FunnelBookmark, edited: FunnelBookmark) {
    const originalFunnel = FunnelsQuery.fromBookmark(original);
    const editedFunnel = FunnelsQuery.fromBookmark(edited);
    return !originalFunnel.equals(editedFunnel);
  }

  public getDefaultBookmarkParams(_project: CurrentProject) {
    return new FunnelsQuery().generateBookmark();
  }

  public isEmptyResults(results) {
    if (!results) {
      return true;
    }

    // temp fix for sankey results
    if (hasData(createFunnelDataWithRename(results.data, new FunnelsQuery()))) {
      return false;
    }

    // Check Sankey
    const sankeyData = Object.values(results?.data || {})[0];
    return !hasSankeyData(sankeyData as any);
  }

  public isQueryable(params: FunnelBookmark) {
    return FunnelsQuery.fromBookmark(params).isValidForQuery();
  }

  public getSegmentationMetadatas(results: FunnelQueryResponse): SegmentationMetadata[] {
    const metas = results.meta?.group_by_metadata;
    return metas?.map?.((meta) => ({
      segment: meta.bookmark,
      numericBucket: meta.numeric_buckets,
    }));
  }

  public handleSegmentFilterControllerChange(
    segmentFilterController: SegmentFilterController,
    params: FunnelBookmark,
  ): FunnelBookmark {
    if (usesFlatSelectedSegments(params)) {
      const selectedSegments: ColorChoice[] = segmentFilterController.getSelectedItems(0).map((item) => {
        return {key: item.id, color: segmentFilterController.getItemColor(0, item.id)};
      });
      params.selectedSegments = selectedSegments;
    }

    return {...params, savedLegend: segmentFilterController.toBookmarkSavedLegend()};
  }

  public shouldResetSegmentFilterSelectMode(original: FunnelBookmark, edited: FunnelBookmark) {
    const query = FunnelsQuery.fromBookmark(original);
    const newQuery = FunnelsQuery.fromBookmark(edited);
    const segmentsChanged = !isEqual(query.getSegmentNames(), newQuery.getSegmentNames());
    const filtersChanged = !isEqual(newQuery.filters, query.filters);
    return segmentsChanged || filtersChanged;
  }

  public getDateRange(params: FunnelBookmark): DateRange {
    return DateRange.fromBookmarkFormat(params.date_range);
  }

  public getBookmarkParamsFromQueryContext(context: QueryContext): FunnelBookmark {
    return getQueryFromQueryContext(context).generateBookmark();
  }

  public getQueryContextFromBookmarkParams({
    params,
    additionalContext,
    originDashboard,
    querySamplingEnabled,
  }: {
    params: FunnelBookmark;
    additionalContext: AdditionalContext;
    originDashboard: DashboardOrigin;
    querySamplingEnabled: boolean;
  }): QueryContext {
    const query = FunnelsQuery.fromBookmark(params);
    query.querySampling = querySamplingEnabled;
    const context = getReportQueryContext(query, originDashboard);
    if (isViewAsFlowsContext(additionalContext)) {
      context.viewAsFlowsContext = additionalContext.viewAsFlowsContext;
    }

    return context;
  }

  public hasDataTable(params: FunnelBookmark): boolean {
    const widgetType = getChartWidgetType(params);
    return hasDataTable(widgetType);
  }

  public getQuerySamplingFactor(results: FunnelQueryResponse): number {
    return getQuerySamplingFactor(results);
  }

  public getClientQueryTrackingProps(params: FunnelBookmark): ClientQueryTrackingProps {
    const query = FunnelsQuery.fromBookmark(params);
    return getFunnelQueryTrackingProps(query);
  }
}
